<script lang="ts" setup>
import HomeView from "@src/components/views/HomeView/HomeView.vue";
import AccessView from "@src/components/views/AccessView/AccessView.vue";
import useStore from "@src/stores/store";
import {useAuthStore} from "@src/stores/auth";
import {onMounted, onUnmounted, ref, watch} from "vue";
import LoadingView from "@src/components/views/LoadingView/LoadingView.vue";
import {useEventListener} from "@vueuse/core";
import LoadingService from "@src/services/loading";
import {requestNativePermissions} from "@src/services/capacitorPermissions";
import {initPushNotifications} from "@src/services/pushNotifications";
import {initBackButton} from "@src/services/capacitorNavigation";
import InactiveView from "@src/components/views/AccessView/InactiveView.vue";
import {App as CapacitorApp} from "@capacitor/app";
import {Capacitor} from "@capacitor/core";
import {reapplyStatusBarStyle} from "@src/services/statusBar";

const store = useStore()
const auth = useAuthStore();

onMounted(async () => {
    await requestNativePermissions();
    initBackButton();

    // carrega do storage primeiro
    auth.loadFromStorage();

    // valida na API
    await auth.getUser();

    if (auth.user) {
        // resyncIfStale roda antes de montar a LoadingView para evitar que o
        // wipe (table.clear()) rode concorrentemente com o bulkPut() da
        // primeira carga — ver docs/superpowers/specs sobre o bug de tela vazia.
        await LoadingService.resyncIfStale();
        store.setStatus("loading");
    } else {
        await store.goToLogin();
    }
});

// Reexecuta o registro de push sempre que o usuário autentica — cobre tanto
// o mount inicial quanto um login sem reiniciar o app após um logout.
// initPushNotifications() é idempotente (não duplica listeners).
watch(() => auth.user, async (user, previousUser) => {
    if (user && !previousUser) {
        await initPushNotifications();
    }
}, {immediate: true});

// change the app height to the window height.
const height = ref(`${window.innerHeight}px`);

// change the app height to the window height.
const resizeWindow = () => {
    height.value = `${window.innerHeight}px`;
};
// and add the resize event when the component mounts.
onMounted(() => {
    window.addEventListener("resize", resizeWindow);
});

// remove the event when un-mounting the component.
onUnmounted(() => {
    window.removeEventListener("resize", resizeWindow);
});

// Sync ao voltar para a aba/app (Revalidation on Focus)
// Útil se o PC hibernou ou a internet caiu e voltou
const revalidateOnForeground = async () => {
    // No iOS, o plugin nativo reseta a status bar pro estilo estático do
    // capacitor.config.ts sempre que a view reaparece (ex.: ao fechar a
    // câmera/seletor de fotos) — reforça o estilo certo aqui.
    reapplyStatusBarStyle();

    if (store.status === 'blocked' || store.status === 'loading') {
        return
    }

    if (!auth.user) {
        return
    }

    const wiped = await LoadingService.resyncIfStale();

    if (wiped) {
        store.setStatus("loading");
        return;
    }

    await LoadingService.syncData();
};

useEventListener(window, "focus", revalidateOnForeground);

// No Capacitor, o WebView nem sempre dispara o evento DOM "focus" ao voltar
// do background — o evento nativo "resume" é o equivalente confiável.
if (Capacitor.isNativePlatform()) {
    CapacitorApp.addListener('resume', revalidateOnForeground);
}

const heartBeat = () => {
    store.sendHeartBeat(localStorage.getItem('session') ?? '', document.visibilityState === 'visible')

    if (document.visibilityState === 'visible') {
        revalidateOnForeground();
    }
}

setInterval((heartBeat), 120000);
</script>

<template>
    <div :class="{ dark: store.settings.darkMode }" class="overflow-hidden">
        <div
            :style="{ height: height }"
            class="bg-white dark:bg-gray-800 transition-colors duration-500 pt-[env(safe-area-inset-top)]"
        >
            <LoadingView v-if="store.status === 'loading'"/>
            <HomeView v-else-if="store.status === 'success'"/>
            <InactiveView v-else-if="store.status === 'blocked'"/>
            <AccessView v-else/>
        </div>
        <div id="modal-root"></div>
    </div>
</template>

<style scoped>
</style>
